import React, { Fragment, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Breadcrumb from "../../../../layout/breadcrumb";
import { Card, CardBody, Col, Container, FormGroup, Row } from "reactstrap";
import CommonDataTable from "../../../../share-components/table/commonDataTable";
import withReducer from "../../../../store/withReducer";
import productReducer from "../../store";
import { useDispatch, useSelector } from "react-redux";
import {
    addSizeCategoryMeasurement,
    closeEditDialog,
    deleteSizeCategoryMeasurement,
    exportSizeCategoryMeasurements,
    getAllSizeCategoryMeasurements,
    getSyncStatus,
    openEditDialog,
    openImportDialog,
    openNewDialog,
    selectSizeCategoryMeasurements,
    selectSyncStatus,
    syncNow,
    updateSizeCategoryMeasurement,
    setFilters,
} from "../../store/sizeCategoryMeasurementSlice";
import ModalFormSubmit from "../../../../share-components/form/modalFormSubmit";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import SizeCategoryMeasurementForm from "./sizeCategoryMeasurementForm";
import SizeCategoryMeasurementImportModal from "./SizeCategoryMeasurementImportModal";
import PreLoader from "../../../../share-components/ui/preLoader";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import {
    brandAutoSearchApi,
    categoryAutoSearchApi,
    getMeasurementTypesApi,
    sellerAutoSearchApi,
    sizeAutoSearchApi,
} from "../../../../services/manage-product/setting/sizeCategoryMeasurementService";

const defaultValues = {
    id: '',
    category_id: null,
    size_matrix_id: null,
    measurement_type: null,
    measurement_unit_type: null,
    measurement_min_value: '',
    measurement_max_value: '',
    uk_size: '',
    eu_size: '',
    us_size: '',
    seller_id: null,
    brand_id: null,
    category: null,
    size: null,
    measurement_type_option: null,
    measurement_unit_option: null,
    seller_option: null,
    brand_option: null,
};

const schema = yup.object().shape({
    category_id: yup.number().nullable().required('Category is required'),
    size_matrix_id: yup.number().nullable().required('Size is required'),
    measurement_type: yup.string().nullable().required('Measurement type is required'),
    measurement_unit_type: yup.string().nullable().required('Unit is required'),
    measurement_min_value: yup.number()
        .typeError('Min value must be a number')
        .min(0, 'Min value must be ≥ 0')
        .required('Min value is required'),
    measurement_max_value: yup.number()
        .typeError('Max value must be a number')
        .min(0, 'Max value must be ≥ 0')
        .nullable()
        .transform((v, o) => o === '' ? null : v)
        .test('max-gte-min', 'Max value must be ≥ min value', function (maxVal) {
            if (maxVal === null || maxVal === undefined) return true;
            const minVal = this.parent.measurement_min_value;
            if (minVal === null || minVal === undefined || isNaN(Number(minVal))) return true;
            return Number(maxVal) >= Number(minVal);
        }),
    uk_size: yup.string().nullable(),
    eu_size: yup.string().nullable(),
    us_size: yup.string().nullable(),
    seller_id: yup.number().nullable()
        .when('$isSuperAdmin', {
            is: true,
            then: (s) => s.required('Seller is required'),
            otherwise: (s) => s.nullable().default(null),
        }),
    brand_id: yup.number().nullable().default(null),
});

const dataMenu = [
    { type: 0, text: 'Products', link: '/product' },
    { type: 1, text: 'Product Setting', link: '' },
    { type: 2, text: 'Size Category Measurement', link: '' },
];

const formatRange = (min, max) => {
    if (!min && min !== 0) return '-';
    return max ? `${min} – ${max}` : `${min}`;
};

const unitBadgeStyle = {
    cm:   { backgroundColor: '#cce5ff', color: '#004085', border: '1px solid #b8daff' },
    inch: { backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', padding: '3px 5px' },
};

const UnitBadge = ({ unit }) => {
    if (!unit) return <span>-</span>;
    const style = unitBadgeStyle[unit] ?? { backgroundColor: '#e2e3e5', color: '#383d41', border: '1px solid #d6d8db', padding: '3px 5px' };
    return (
        <span className="badge" style={style}>
            {unit}
        </span>
    );
};

const SizeCategoryMeasurement = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [buttonDisable, setButtonDisable] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [activeFilters, setActiveFilters] = useState({});
    const [measurementTypeOptions, setMeasurementTypeOptions] = useState([]);
    const unitOptions = [
        { value: 'cm',   label: 'cm' },
        { value: 'inch', label: 'inch' },
    ];
    const [brandsCount, setBrandsCount] = useState(0);

    const measurements = useSelector(selectSizeCategoryMeasurements);
    const reduxFilters = useSelector(({ product }) => product.sizeCategoryMeasurement.filters);
    const syncStatus = useSelector(selectSyncStatus);
    const currentUser = useSelector(({ authReducer }) => authReducer.data.user);
    const isSuperAdmin = currentUser.role.id === 1;

    const showBrandFilter = isSuperAdmin || brandsCount > 1;

    const { handleSubmit, formState, reset, register, setValue, trigger, getValues } = useForm({
        mode: 'onChange',
        defaultValues,
        resolver: yupResolver(schema),
        context: { isSuperAdmin },
    });
    const { errors } = formState;

    const getNextAutoRun = () => {
        const next = new Date();
        next.setHours(next.getHours() + 1, 0, 0, 0);
        const mins = Math.round((next - new Date()) / 60000);
        return `in ${mins} min`;
    };

    const handleSyncNow = () => {
        if (syncLoading) return;
        setSyncLoading(true);
        dispatch(syncNow()).then(() => {
            setSyncLoading(false);
            toast.success('Sync complete', { position: toast.POSITION.TOP_RIGHT });
        });
    };

    useEffect(() => {
        dispatch(getAllSizeCategoryMeasurements({})).then((res) => {
            if (!res.error) setLoading(false);
        });
        dispatch(getSyncStatus());

        getMeasurementTypesApi().then(({ response }) => {
            setMeasurementTypeOptions(
                (Array.isArray(response) ? response : []).map(t => ({ value: t.name, label: t.display_name }))
            );
        });

        if (!isSuperAdmin) {
            brandAutoSearchApi({ value: '' }).then(({ response }) => {
                setBrandsCount(Array.isArray(response) ? response.length : 0);
            });
        }
    }, [dispatch]);

    const applyFilter = (patch) => {
        const merged = { ...activeFilters, ...patch };
        const cleaned = Object.fromEntries(
            Object.entries(merged).filter(([, v]) => v != null && v !== '')
        );
        setActiveFilters(cleaned);
        dispatch(setFilters(cleaned));
        dispatch(getAllSizeCategoryMeasurements(cleaned));
    };

    const clearFilters = (e) => {
        e.preventDefault();
        setActiveFilters({});
        dispatch(setFilters({}));
        dispatch(getAllSizeCategoryMeasurements({}));
    };

    const hasActiveFilters = Object.keys(activeFilters).length > 0;

    const categoryFilterPromise = (inputValue) =>
        categoryAutoSearchApi({ value: inputValue }).then(({ response }) =>
            Array.isArray(response) ? response.map((c) => ({ value: c.id, label: c.name })) : []
        );

    const sizeFilterPromise = (inputValue) =>
        sizeAutoSearchApi({ value: inputValue }).then(({ response }) =>
            Array.isArray(response) ? response.map((s) => ({ value: s.id, label: s.name })) : []
        );

    const sellerFilterPromise = (inputValue) =>
        sellerAutoSearchApi({ value: inputValue }).then(({ response }) =>
            Array.isArray(response) ? response.map((s) => ({ value: s.id, label: s.shop_name || s.name })) : []
        );

    const brandFilterPromise = (inputValue) =>
        brandAutoSearchApi({ value: inputValue, seller_id: activeFilters.seller_id ?? null }).then(({ response }) =>
            Array.isArray(response) ? response.map((b) => ({ value: b.id, label: b.name })) : []
        );

    const tableColumns = [
        {
            name: 'Category',
            selector: (row) => row.category?.name,
            sortable: true,
            left: true,
        },
        {
            name: 'Size',
            selector: (row) => row.size_matrix?.name,
            sortable: true,
            left: true,
            width: '100px',
        },
        {
            name: 'Type',
            selector: (row) => row.measurement_type,
            sortable: true,
            left: true,
        },
        {
            name: 'Unit',
            cell: (row) => <UnitBadge unit={row.measurement_unit_type} />,
            center: true,
            width: '100px',
        },
        {
            name: 'Range',
            selector: (row) => formatRange(row.measurement_min_value, row.measurement_max_value),
            left: true,
        },
        {
            name: 'UK',
            selector: (row) => row.uk_size ?? '—',
            left: true,
            width: '60px',
        },
        {
            name: 'EU',
            selector: (row) => row.eu_size ?? '—',
            left: true,
            width: '60px',
        },
        {
            name: 'US',
            selector: (row) => row.us_size ?? '—',
            left: true,
            width: '60px',
        },
        ...(isSuperAdmin ? [
            {
                name: 'Seller',
                cell: (row) => row.seller
                    ? <span>{row.seller.shop_name}</span>
                    : <span className="text-muted">Platform</span>,
                left: true,
            },
        ] : []),
        ...(showBrandFilter ? [
            {
                name: 'Brand',
                selector: (row) => row.seller_brand?.name ?? '—',
                left: true,
            },
        ] : []),
    ];

    const onSubmitForm = (dialog, data) => {
        setButtonDisable(true);
        const payload = {
            ...data,
            seller_id: data.seller_id || 0,
            brand_id: data.brand_id || 0,
        };
        if (dialog.type === 'new') {
            dispatch(addSizeCategoryMeasurement(payload)).then((res) => {
                setButtonDisable(false);
                if (!res.error) closeDialog();
            });
        } else {
            dispatch(updateSizeCategoryMeasurement({ ...payload, id: data.id })).then((res) => {
                setButtonDisable(false);
                if (!res.error) closeDialog();
            });
        }
    };

    const onEdit = (row) => {
        setFormLoading(true);
        dispatch(openEditDialog({
            ...row,
            category: row.category ? { value: row.category.id, label: row.category.name } : null,
            size: row.size_matrix ? { value: row.size_matrix.id, label: row.size_matrix.name } : null,
            measurement_type_option: row.measurement_type ? { value: row.measurement_type, label: row.measurement_type } : null,
            measurement_unit_option: row.measurement_unit_type ? { value: row.measurement_unit_type, label: row.measurement_unit_type } : null,
            seller_option: row.seller ? { value: row.seller.id, label: row.seller.shop_name } : null,
            brand_option: row.seller_brand ? { value: row.seller_brand.id, label: row.seller_brand.name } : null,
        }));
    };

    const onDelete = (row) => {
        dispatch(deleteSizeCategoryMeasurement(row.id));
    };

    const closeDialog = () => {
        if (buttonDisable) return;
        dispatch(closeEditDialog());
    };

    const handleExport = () => {
        if (exportLoading) return;
        setExportLoading(true);
        dispatch(exportSizeCategoryMeasurements(reduxFilters)).then((res) => {
            setExportLoading(false);
            if (res.payload) {
                const url = window.URL.createObjectURL(new Blob([res.payload]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `size-category-measurements-${new Date().toISOString().slice(0, 10)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
        });
    };

    return (
        <Fragment>
            <Container fluid={true} className="product-wrapper">
                {loading ? <PreLoader /> : (
                    <>
                        <Breadcrumb data={dataMenu} title="Size Category Measurement" />
                        <div className="product-grid">
                            <div className="feature-products">

                                {/* Action buttons */}
                                <Row>
                                    <Col sm="12">
                                        <Card>
                                            <Row>
                                                <Col sm="6" />
                                                <Col sm="6">
                                                    <div className="float-sm-right p-3">
                                                        <a
                                                            className="btn btn-sm btn-outline-secondary mr-2"
                                                            onClick={handleExport}
                                                            style={{ pointerEvents: exportLoading ? 'none' : 'auto' }}
                                                        >
                                                            {exportLoading ? (
                                                                <i className="fa fa-spinner fa-spin mr-1" />
                                                            ) : (
                                                                <i className="fa fa-download mr-1" />
                                                            )} Export
                                                        </a>
                                                        {(currentUser.permission).includes('Create Size Category Measurement') && (
                                                            <>
                                                                <a
                                                                    className="btn btn-sm btn-secondary mr-2"
                                                                    onClick={() => dispatch(openImportDialog())}
                                                                >
                                                                    <i className="fa fa-upload" /> Import
                                                                </a>
                                                                <a
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={() => { setFormLoading(true); dispatch(openNewDialog()); }}
                                                                >
                                                                    <i className="fa fa-plus" /> Add Measurement
                                                                </a>
                                                            </>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row className="px-3 pb-3">
                                                <Col className="d-flex align-items-center flex-wrap" style={{ gap: '16px' }}>
                                                    <a
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={handleSyncNow}
                                                        style={{ pointerEvents: syncLoading ? 'none' : 'auto' }}
                                                    >
                                                        {syncLoading
                                                            ? <><i className="fa fa-spinner fa-spin mr-1" />Syncing&hellip;</>
                                                            : <><i className="fa fa-refresh mr-1" />Sync to Shopify</>}
                                                    </a>
                                                    <small className="text-muted">
                                                        Last synced:{' '}
                                                        <strong>
                                                            {syncStatus?.lastSyncedAt
                                                                ? new Date(syncStatus.lastSyncedAt).toLocaleString()
                                                                : 'Never'}
                                                        </strong>
                                                    </small>
                                                    <small className="text-muted">
                                                        Next auto-run: <strong>{getNextAutoRun()}</strong>
                                                    </small>
                                                    {syncStatus?.pendingCount > 0 && (
                                                        <small className="text-warning">
                                                            <i className="fa fa-clock-o mr-1" />
                                                            {syncStatus.pendingCount} record(s) pending sync
                                                        </small>
                                                    )}
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Filters */}
                                <Row>
                                    <Col sm="12">
                                        <Card>
                                            <CardBody>
                                                <Row className="mb-2">
                                                    <Col md={4}>
                                                        <FormGroup className="m-0">
                                                            <label className="col-form-label pt-0 pb-1 small">Category</label>
                                                            <AsyncSelect
                                                                isClearable
                                                                cacheOptions
                                                                defaultOptions
                                                                loadOptions={categoryFilterPromise}
                                                                value={activeFilters.category_id
                                                                    ? { value: activeFilters.category_id, label: activeFilters._categoryLabel }
                                                                    : null}
                                                                onChange={(e) => applyFilter({
                                                                    category_id: e?.value ?? null,
                                                                    _categoryLabel: e?.label ?? null,
                                                                })}
                                                                placeholder="All categories..."
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup className="m-0">
                                                            <label className="col-form-label pt-0 pb-1 small">Size</label>
                                                            <AsyncSelect
                                                                isClearable
                                                                cacheOptions
                                                                defaultOptions
                                                                loadOptions={sizeFilterPromise}
                                                                value={activeFilters.size_matrix_id
                                                                    ? { value: activeFilters.size_matrix_id, label: activeFilters._sizeLabel }
                                                                    : null}
                                                                onChange={(e) => applyFilter({
                                                                    size_matrix_id: e?.value ?? null,
                                                                    _sizeLabel: e?.label ?? null,
                                                                })}
                                                                placeholder="All sizes..."
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    <Col md={4}>
                                                        <FormGroup className="m-0">
                                                            <label className="col-form-label pt-0 pb-1 small">Measurement Type</label>
                                                            <Select
                                                                isClearable
                                                                options={measurementTypeOptions}
                                                                value={activeFilters.measurement_type
                                                                    ? measurementTypeOptions.find(o => o.value === activeFilters.measurement_type) ?? null
                                                                    : null}
                                                                onChange={(e) => applyFilter({ measurement_type: e?.value ?? null })}
                                                                placeholder="All types..."
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                                <Row className="align-items-end">
                                                    <Col md={3}>
                                                        <FormGroup className="m-0">
                                                            <label className="col-form-label pt-0 pb-1 small">Unit</label>
                                                            <Select
                                                                isClearable
                                                                options={unitOptions}
                                                                value={activeFilters.measurement_unit_type
                                                                    ? unitOptions.find(o => o.value === activeFilters.measurement_unit_type) ?? null
                                                                    : null}
                                                                onChange={(e) => applyFilter({ measurement_unit_type: e?.value ?? null })}
                                                                placeholder="All units..."
                                                            />
                                                        </FormGroup>
                                                    </Col>
                                                    {isSuperAdmin && (
                                                        <Col md={3}>
                                                            <FormGroup className="m-0">
                                                                <label className="col-form-label pt-0 pb-1 small">Seller</label>
                                                                <AsyncSelect
                                                                    isClearable
                                                                    cacheOptions
                                                                    defaultOptions
                                                                    loadOptions={sellerFilterPromise}
                                                                    value={activeFilters.seller_id
                                                                        ? { value: activeFilters.seller_id, label: activeFilters._sellerLabel }
                                                                        : null}
                                                                    onChange={(e) => applyFilter({
                                                                        seller_id:   e?.value ?? null,
                                                                        _sellerLabel: e?.label ?? null,
                                                                        brand_id:    null,
                                                                        _brandLabel: null,
                                                                    })}
                                                                    placeholder="All sellers..."
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    )}
                                                    {showBrandFilter && (
                                                        <Col md={3}>
                                                            <FormGroup className="m-0">
                                                                <label className="col-form-label pt-0 pb-1 small">Brand</label>
                                                                <AsyncSelect
                                                                    key={activeFilters.seller_id ?? 'all'}
                                                                    isClearable
                                                                    cacheOptions
                                                                    defaultOptions
                                                                    loadOptions={brandFilterPromise}
                                                                    value={activeFilters.brand_id
                                                                        ? { value: activeFilters.brand_id, label: activeFilters._brandLabel }
                                                                        : null}
                                                                    onChange={(e) => applyFilter({
                                                                        brand_id:    e?.value ?? null,
                                                                        _brandLabel: e?.label ?? null,
                                                                    })}
                                                                    placeholder="All brands..."
                                                                />
                                                            </FormGroup>
                                                        </Col>
                                                    )}
                                                    {hasActiveFilters && (
                                                        <Col md="auto" className="mb-1">
                                                            <a href="#" className="text-danger small" onClick={clearFilters}>
                                                                <i className="fa fa-times mr-1" />Clear filters
                                                            </a>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Table */}
                                <Row>
                                    <Col sm="12">
                                        <Card>
                                            <CardBody>
                                                <div className="table-responsive product-table mt-2">
                                                    <CommonDataTable
                                                        headerColumns={tableColumns}
                                                        gridData={measurements}
                                                        pagination
                                                        onEdit={(currentUser.permission).includes('Update Size Category Measurement') ? onEdit : ''}
                                                        onDelete={(currentUser.permission).includes('Delete Size Category Measurement') ? onDelete : ''}
                                                    />
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                </Row>

                            </div>
                        </div>
                    </>
                )}
            </Container>

            <ModalFormSubmit
                defaultValues={defaultValues}
                schema={schema}
                onSubmitForm={onSubmitForm}
                reset={reset}
                formState={formState}
                handleSubmit={handleSubmit}
                title="Size Category Measurement"
                selector="product"
                dialog={({ product }) => product.sizeCategoryMeasurement.addDialog}
                closeDialog={closeDialog}
                setValue={setValue}
                getValues={getValues}
                buttonDisable={buttonDisable || formLoading}
            >
                <SizeCategoryMeasurementForm
                    errors={errors}
                    register={register}
                    setValue={setValue}
                    trigger={trigger}
                    getValues={getValues}
                    isSuperAdmin={isSuperAdmin}
                    onLoadingChange={setFormLoading}
                />
            </ModalFormSubmit>

            <SizeCategoryMeasurementImportModal />
        </Fragment>
    );
};

export default withReducer([{ sizeCategoryMeasurement: productReducer }])(SizeCategoryMeasurement);
